import { PageSpecific } from '.generated/Feature.CW.model';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-nextjs';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useDictionary from 'lib/hooks/useDictionary';
import moment from 'moment-timezone';
import Link from 'next/link';
import useIsEditing from 'lib/hooks/useIsEditing';

export type EvenetBannerProps = PageSpecific.EventsBanner & {
  params?: { DynamicPlaceholderId?: string };
  rendering: ComponentRendering;
};

const EventsBanner = (props: EvenetBannerProps): JSX.Element => {
  const { DynamicPlaceholderId } = props?.params || {};
  const { getDictionaryValue } = useDictionary();
  const isEditing = useIsEditing();
  const phKey = `socialsharelinks-${DynamicPlaceholderId}`;
  const formatEventDate = (dateString: string): string => {
    return moment(dateString).format('MMMM D');
  };

  const formatCentralTimeCSTLabel = (iso: string) =>
    moment.utc(iso).tz('America/Chicago').format('h:mm A') + ' (CT)';

  const isMidnightTime = (iso?: string): boolean =>
    typeof iso === 'string' && /T00:00:00/.test(iso);

  const formatTimeIfNotMidnight = (iso?: string): string | undefined => {
    if (!iso || isMidnightTime(iso)) return undefined;
    return formatCentralTimeCSTLabel(iso);
  };

  const startDate = props?.fields?.startDateTme?.value;
  const endDate = props?.fields?.endDateTime?.value;

  const startTimeLabel = formatTimeIfNotMidnight(startDate);
  const endTimeLabel = formatTimeIfNotMidnight(endDate);
  const shouldShowTimeRow = Boolean(startTimeLabel || endTimeLabel);

  return (
    <div className="bg-lightaqua-50  py-[32px]" role="region">
      <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10">
        <div className="bg-white rounded-[8px] shadow-default">
          <div className="flex flex-col lg:flex-row  gap-[32px] lg:gap-[40px] p-[32px] justify-between">
            <div>
              <h3 className="text-14 leading-22 font-bold font-nunito text-gray-900 mb-[8px]">
                Location
              </h3>
              {props?.fields?.locationName?.value && (
                <div className="flex gap-[8px] lg:w-[365px]">
                  <SvgIcon
                    icon="map-pin"
                    className="w-[24px] h-[24px] mt-[2px] text-orange-700"
                    size="lg"
                    ariaHidden={true}
                  />
                  <PlainTextWrapper
                    field={props?.fields?.locationName}
                    className="flex-1 text-16 leading-25 text-gray-900 font-nunito"
                    tag="div"
                  />
                </div>
              )}
              <div
                className={`lg:w-[365px]  ${props?.fields?.locationName?.value ? 'pl-[32px]' : ''}`}
              >
                <PlainTextWrapper
                  field={props?.fields?.address}
                  className="flex-1 text-16 leading-25 text-gray-900 font-nunito"
                  tag="div"
                />
              </div>

              {isEditing ? (
                <div
                  className={`text-16 leading-25 font-nunito text-gray-900 pt-[13px] gap-[8px] flex items-center ${props?.fields?.locationName?.value ? 'pl-[32px]' : ''}`}
                >
                  {getDictionaryValue('GetDirections')}
                  <SvgIcon icon="new-tab" size="md" className="font-bold" />
                </div>
              ) : (
                props?.fields?.latitude?.value &&
                props?.fields?.longitude?.value && (
                  <Link
                    target="_blank"
                    href={`https://www.google.com/maps/?q=${props?.fields?.latitude?.value},-${props?.fields?.longitude?.value}`}
                    className={`text-16 leading-25 font-nunito text-gray-900 pt-[13px] hover:underline gap-[8px] flex items-center ${props?.fields?.locationName?.value ? 'pl-[32px]' : ''}`}
                  >
                    {getDictionaryValue('GetDirections')}
                    <SvgIcon icon="new-tab" size="md" className="font-bold" />
                  </Link>
                )
              )}
            </div>
            <div>
              <h3 className="text-14 leading-22 font-bold font-nunito text-gray-900 mb-[8px]">
                Date and Time
              </h3>
              <div>
                <div className="flex items-center gap-[8px]">
                  <SvgIcon
                    icon="calendar-blank"
                    className="w-[24px] h-[24px] mt-[2px] text-orange-700"
                    size="md"
                    ariaHidden={true}
                  />
                  <time dateTime={startDate || ''} className="flex-1">
                    {formatEventDate(startDate || '')}{' '}
                    {endDate &&
                      endDate?.split('T')[0] !== startDate?.split('T')[0] &&
                      `- ${formatEventDate(endDate)}`}
                  </time>
                </div>
                {shouldShowTimeRow && (
                  <div className="flex gap-[8px] pt-[8px]">
                    <SvgIcon
                      icon="clock-bold"
                      className="w-[24px] h-[24px] mt-[2px] text-orange-700"
                      size="md"
                      ariaHidden={true}
                    />
                    <time className="flex-1">
                      {formatCentralTimeCSTLabel(startDate || '')}{' '}
                      {endDate && `- ${formatCentralTimeCSTLabel(endDate)}`}
                    </time>
                  </div>
                )}
              </div>
            </div>
            <div>
              <PlaceholderWrapper name={phKey} rendering={props.rendering} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventsBanner;
